import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useXP } from "@/hooks/useXP";
import { useHealth } from "@/hooks/useHealth";
import { useChallenges } from "@/hooks/useChallenges";
import { getCompletedLevels, getLevelBestScore, LEVELS, ALL_LEVELS, getLevelStars } from "@/config/levels";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { XPBadge } from "@/components/game/XPDisplay";
import { 
  ArrowLeft, 
  Star, 
  Trophy, 
  Target, 
  Zap, 
  Award,
  RotateCcw,
  Heart,
  Sparkles,
  Crown,
  Cherry,
  Shield
} from "lucide-react";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    totalXP, 
    lifetimeXP, 
    level, 
    currentXP, 
    neededXP, 
    prestige,
    isMaxLevel,
    isWaitingForPrestige,
  } = useXP();
  const { hp, maxHP } = useHealth();
  const { getFruitLevels } = useChallenges();
  
  const fruitLevels = getFruitLevels();
  const completedLevels = getCompletedLevels();
  const totalLevels = LEVELS.length;
  
  // Calculate total stars earned
  const totalStars = completedLevels.reduce((acc, levelId) => {
    const score = getLevelBestScore(levelId);
    const level = LEVELS.find(l => l.id === levelId);
    if (!level) return acc;
    const baseScore = level.targetCount * 10;
    if (score >= baseScore * 3) return acc + 3;
    if (score >= baseScore * 2) return acc + 2;
    if (score >= baseScore) return acc + 1;
    return acc;
  }, 0);
  
  // Calculate total possible stars
  const maxPossibleStars = totalLevels * 3;
  
  // Get total score from all levels
  const totalScore = LEVELS.reduce((acc, level) => {
    return acc + getLevelBestScore(level.id);
  }, 0);
  
  // Prestige emblem configuration (same as leaderboard)
  const getPrestigeEmblem = (prestigeLevel) => {
    // Prestige Master emblem (6+)
    if (prestigeLevel >= 6) {
      return {
        symbol: 'M',
        name: 'Prestige Master',
        bgGradient: 'from-amber-400 via-yellow-300 to-amber-500',
        textColor: 'text-amber-900',
        borderColor: 'border-amber-300',
        shadowColor: 'shadow-amber-400/50',
        animate: 'animate-pulse'
      };
    }
    
    const emblems = {
      1: { 
        symbol: 'I', 
        name: 'Prestige I', 
        bgGradient: 'from-emerald-500 to-green-600',
        textColor: 'text-white',
        borderColor: 'border-emerald-300',
        shadowColor: 'shadow-emerald-400/50'
      },
      2: { 
        symbol: 'II', 
        name: 'Prestige II', 
        bgGradient: 'from-sky-500 to-blue-600',
        textColor: 'text-white',
        borderColor: 'border-sky-300',
        shadowColor: 'shadow-sky-400/50'
      },
      3: { 
        symbol: 'III', 
        name: 'Prestige III', 
        bgGradient: 'from-violet-500 to-purple-600',
        textColor: 'text-white',
        borderColor: 'border-violet-300',
        shadowColor: 'shadow-violet-400/50'
      },
      4: { 
        symbol: 'IV', 
        name: 'Prestige IV', 
        bgGradient: 'from-rose-500 to-pink-600',
        textColor: 'text-white',
        borderColor: 'border-rose-300',
        shadowColor: 'shadow-rose-400/50'
      },
      5: { 
        symbol: 'V', 
        name: 'Prestige V', 
        bgGradient: 'from-cyan-400 via-blue-400 to-purple-500',
        textColor: 'text-white',
        borderColor: 'border-cyan-200',
        shadowColor: 'shadow-cyan-400/50',
        shimmer: true
      }
    };
    return emblems[prestigeLevel] || null;
  };
  
  const prestigeEmblem = prestige > 0 ? getPrestigeEmblem(prestige) : null;

  return (
    <div className="min-h-screen game-gradient-bg pattern-dots">
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Header */}
        <header className="flex items-center justify-between mb-6">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            size="sm"
            className="font-body"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="font-display font-bold text-xl text-foreground">Profile</h1>
          <div className="w-16" /> {/* Spacer for centering */}
        </header>

        {/* Player Card */}
        <Card className="p-6 mb-6 bg-card border-2 border-primary/20">
          <div className="flex items-center gap-4 mb-4">
            {/* Avatar with prestige indicator */}
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary via-accent to-warning flex items-center justify-center shadow-lg border-4 border-white/50">
                <span className="text-3xl font-display font-bold text-white">
                  {user?.playerName?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
              {prestige > 0 && (
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-warning flex items-center justify-center shadow-md border-2 border-white">
                  <Crown className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="font-display font-bold text-2xl text-foreground">
                  {user?.playerName || 'Player'}
                </h2>
                {prestigeEmblem && (
                  <span 
                    className={`px-2.5 py-0.5 rounded-lg text-sm font-bold bg-gradient-to-r ${prestigeEmblem.bgGradient} ${prestigeEmblem.textColor} border ${prestigeEmblem.borderColor} shadow-md ${prestigeEmblem.shadowColor} ${prestigeEmblem.animate || ''} ${prestigeEmblem.shimmer ? 'shimmer' : ''}`}
                    title={prestigeEmblem.name}
                  >
                    {prestigeEmblem.symbol}
                  </span>
                )}
              </div>
              <p className="font-body text-sm text-muted-foreground mb-2">Fruit Crusher</p>
              <XPBadge 
                level={level} 
                currentXP={currentXP} 
                neededXP={neededXP} 
                totalXP={totalXP}
                prestige={prestige}
                showDetails={true}
              />
            </div>
          </div>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Total XP */}
          <Card className="p-4 bg-card border border-warning/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center">
                <Zap className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="font-body text-xs text-muted-foreground">Total XP</p>
                <p className="font-display font-bold text-lg text-foreground">
                  {totalXP.toLocaleString()}
                </p>
              </div>
            </div>
          </Card>
          
          {/* Lifetime XP */}
          <Card className="p-4 bg-card border border-accent/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="font-body text-xs text-muted-foreground">Lifetime XP</p>
                <p className="font-display font-bold text-lg text-foreground">
                  {lifetimeXP.toLocaleString()}
                </p>
              </div>
            </div>
          </Card>
          
          {/* Levels Completed */}
          <Card className="p-4 bg-card border border-success/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="font-body text-xs text-muted-foreground">Levels Done</p>
                <p className="font-display font-bold text-lg text-foreground">
                  {completedLevels.length} / {totalLevels}
                </p>
              </div>
            </div>
          </Card>
          
          {/* Stars Collected */}
          <Card className="p-4 bg-card border border-primary/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Star className="w-5 h-5 text-primary fill-primary" />
              </div>
              <div>
                <p className="font-body text-xs text-muted-foreground">Stars</p>
                <p className="font-display font-bold text-lg text-foreground">
                  {totalStars} / {maxPossibleStars}
                </p>
              </div>
            </div>
          </Card>
          
          {/* Total Score */}
          <Card className="p-4 bg-card border border-destructive/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-destructive/20 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="font-body text-xs text-muted-foreground">Best Score</p>
                <p className="font-display font-bold text-lg text-foreground">
                  {totalScore.toLocaleString()}
                </p>
              </div>
            </div>
          </Card>
          
          {/* HP */}
          <Card className="p-4 bg-card border border-pink-300">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                <Heart className="w-5 h-5 text-pink-500 fill-pink-500" />
              </div>
              <div>
                <p className="font-body text-xs text-muted-foreground">HP</p>
                <p className="font-display font-bold text-lg text-foreground">
                  {hp} / {maxHP}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* World Mastery Badges */}
        <Card className="p-4 mb-6 bg-card border border-border" data-testid="world-mastery-badges">
          <h3 className="font-display font-semibold text-foreground mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-500" />
            World Mastery Badges
          </h3>
          <p className="font-body text-xs text-muted-foreground mb-4">
            Earn 3 stars on every level in a world to unlock its mastery badge.
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {(() => {
              const WORLD_DEFS = [
                { id: 1, name: 'Fruit Garden', emoji: '🌸', range: [1, 50], color: 'from-green-400 to-emerald-500' },
                { id: 2, name: 'Ice World', emoji: '🧊', range: [51, 60], color: 'from-cyan-400 to-sky-500' },
                { id: 3, name: 'Chain Realm', emoji: '⛓️', range: [61, 70], color: 'from-slate-400 to-gray-500' },
                { id: 4, name: 'Chocolate', emoji: '🍫', range: [71, 80], color: 'from-orange-400 to-amber-600' },
                { id: 5, name: 'Honey Hive', emoji: '🍯', range: [81, 90], color: 'from-yellow-400 to-amber-500' },
                { id: 6, name: 'Lava World', emoji: '🌋', range: [91, 100], color: 'from-red-500 to-orange-600' },
                { id: 7, name: 'Point World', emoji: '🎯', range: [101, 120], color: 'from-purple-500 to-violet-600' },
                { id: 8, name: 'Dark Forest', emoji: '🦉', range: [121, 140], color: 'from-green-700 to-emerald-900' },
                { id: 9, name: 'Ocean Depths', emoji: '🌊', range: [141, 160], color: 'from-blue-500 to-indigo-600' },
                { id: 10, name: 'Frozen Peaks', emoji: '❄️', range: [161, 180], color: 'from-sky-300 to-blue-500' },
                { id: 11, name: 'Ancient Ruins', emoji: '🏛️', range: [181, 200], color: 'from-amber-600 to-stone-700' },
                { id: 12, name: 'Volcanic Core', emoji: '🔥', range: [201, 220], color: 'from-red-600 to-red-800' },
                { id: 13, name: 'Moonlight', emoji: '🌙', range: [221, 240], color: 'from-indigo-400 to-purple-600' },
                { id: 14, name: 'Alchemist', emoji: '⚗️', range: [241, 260], color: 'from-emerald-400 to-teal-600' },
                { id: 15, name: 'Circus', emoji: '🎪', range: [261, 280], color: 'from-pink-400 to-rose-600' },
                { id: 16, name: 'Shipwreck', emoji: '🏴‍☠️', range: [281, 300], color: 'from-stone-400 to-stone-600' },
                { id: 17, name: 'Temple', emoji: '🌸', range: [301, 320], color: 'from-pink-300 to-rose-500' },
                { id: 18, name: 'Storm', emoji: '⚡', range: [321, 340], color: 'from-gray-500 to-yellow-500' },
                { id: 19, name: 'Cavern', emoji: '🍄', range: [341, 360], color: 'from-violet-500 to-purple-700' },
                { id: 20, name: 'Casino', emoji: '🎰', range: [361, 380], color: 'from-amber-400 to-yellow-600' },
                { id: 21, name: 'Cosmos', emoji: '🌌', range: [381, 400], color: 'from-purple-500 to-pink-500' },
              ];
              return WORLD_DEFS.map(w => {
                const worldLevels = ALL_LEVELS.filter(l => l.id >= w.range[0] && l.id <= w.range[1]);
                const totalLevelsInWorld = worldLevels.length;
                const threeStarred = worldLevels.filter(l => {
                  const score = getLevelBestScore(l.id);
                  const baseScore = l.targetCount * 10;
                  return score >= baseScore * 3;
                }).length;
                const completed = worldLevels.filter(l => completedLevels.includes(l.id)).length;
                const isMastered = threeStarred === totalLevelsInWorld && totalLevelsInWorld > 0;
                const progress = totalLevelsInWorld > 0 ? Math.round((threeStarred / totalLevelsInWorld) * 100) : 0;
                return (
                  <div key={w.id} className={`relative flex flex-col items-center p-3 rounded-xl border transition-all ${
                    isMastered 
                      ? `bg-gradient-to-b ${w.color} border-amber-400 shadow-lg shadow-amber-500/20` 
                      : completed > 0 
                        ? 'bg-muted/50 border-border hover:border-primary/30' 
                        : 'bg-muted/20 border-border/30 opacity-50'
                  }`} data-testid={`world-badge-${w.id}`}>
                    <div className={`text-2xl ${isMastered ? 'animate-bounce' : ''}`} style={isMastered ? { animationDuration: '2s' } : {}}>
                      {w.emoji}
                    </div>
                    <p className={`text-[10px] font-bold mt-1 text-center leading-tight ${isMastered ? 'text-white' : 'text-foreground'}`}>
                      {w.name}
                    </p>
                    <p className={`text-[9px] mt-0.5 ${isMastered ? 'text-white/80' : 'text-muted-foreground'}`}>
                      {threeStarred}/{totalLevelsInWorld}
                    </p>
                    {/* Mini progress bar */}
                    {!isMastered && completed > 0 && (
                      <div className="w-full h-1 bg-muted rounded-full mt-1 overflow-hidden">
                        <div className={`h-full rounded-full bg-gradient-to-r ${w.color}`} style={{ width: `${progress}%` }} />
                      </div>
                    )}
                    {isMastered && (
                      <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center shadow-md border border-white">
                        <Star className="w-3 h-3 text-white fill-white" />
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </Card>

        {/* Prestige Section */}
        {prestigeEmblem && (
          <Card className={`p-4 mb-6 bg-gradient-to-r ${prestigeEmblem.bgGradient} border-2 ${prestigeEmblem.borderColor} shadow-lg ${prestigeEmblem.shadowColor}`}>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-white/30 flex items-center justify-center backdrop-blur-sm">
                <span className={`text-2xl font-black ${prestigeEmblem.textColor}`}>{prestigeEmblem.symbol}</span>
              </div>
              <div>
                <p className={`font-display font-bold text-lg ${prestigeEmblem.textColor}`}>{prestigeEmblem.name}</p>
                <p className={`font-body text-sm ${prestigeEmblem.textColor} opacity-90`}>
                  You've reached the pinnacle {prestige} time{prestige > 1 ? 's' : ''}!
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Prestige Info */}
        {prestigeEmblem && (
          <Card className="p-4 mb-6 bg-card border-2 border-border">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${prestigeEmblem.bgGradient} flex items-center justify-center shadow-lg ${prestigeEmblem.shadowColor}`}>
                <span className={`text-2xl font-black ${prestigeEmblem.textColor}`}>{prestigeEmblem.symbol}</span>
              </div>
              <div>
                <h3 className="font-display font-bold text-lg text-foreground">
                  {prestigeEmblem.name}
                </h3>
                <p className="font-body text-sm text-muted-foreground">
                  {isMaxLevel 
                    ? "Maximum prestige reached! 👑" 
                    : isWaitingForPrestige 
                      ? `${(100000 - currentXP).toLocaleString()} XP until next Prestige`
                      : `Level ${level}/50 • ${prestige < 5 ? `${5 - prestige} prestiges remaining` : prestige >= 6 ? 'Prestige Master!' : 'Final prestige!'}`
                  }
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Fruit Levels Section */}
        <Card className="p-4 mb-6 bg-card border border-border">
          <h3 className="font-display font-semibold text-foreground mb-4 flex items-center gap-2">
            <Cherry className="w-5 h-5 text-pink-500" />
            Fruit Mastery Levels
          </h3>
          <p className="font-body text-xs text-muted-foreground mb-4">
            Eliminate fruits to level up each type! Higher levels = more mastery.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fruitLevels.map(fruit => (
              <div 
                key={fruit.id}
                className="p-3 rounded-xl bg-gradient-to-r from-muted/50 to-muted/30 border border-border/50 hover:border-primary/30 transition-all"
              >
                <div className="flex items-center gap-3">
                  {/* Fruit emoji with level badge */}
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-white to-gray-100 flex items-center justify-center shadow-md border-2 border-white/50">
                      <span className="text-2xl">{fruit.emoji}</span>
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center shadow-sm border border-white">
                      {fruit.level}
                    </div>
                  </div>
                  
                  {/* Fruit info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-display font-semibold text-sm text-foreground">
                        {fruit.name}
                      </span>
                      <span className="font-body text-xs text-muted-foreground">
                        Lv. {fruit.level}/100
                      </span>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-primary via-accent to-primary rounded-full transition-all duration-300"
                        style={{ width: `${fruit.percentage}%` }}
                      />
                    </div>
                    
                    {/* Stats */}
                    <div className="flex justify-between mt-1">
                      <span className="font-body text-[10px] text-muted-foreground">
                        {fruit.total.toLocaleString()} eliminated
                      </span>
                      {!fruit.isMaxLevel && (
                        <span className="font-body text-[10px] text-muted-foreground">
                          {fruit.current}/{fruit.needed} to next
                        </span>
                      )}
                      {fruit.isMaxLevel && (
                        <span className="font-body text-[10px] text-warning font-semibold">
                          MAX LEVEL! 🏆
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Total fruit level summary */}
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <span className="font-body text-sm text-muted-foreground">Total Fruit Mastery</span>
              <span className="font-display font-bold text-foreground">
                {fruitLevels.reduce((sum, f) => sum + f.level, 0)} / {fruitLevels.length * 100}
              </span>
            </div>
            <div className="relative h-3 bg-muted rounded-full overflow-hidden mt-2">
              <div 
                className="h-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-full transition-all duration-300"
                style={{ width: `${(fruitLevels.reduce((sum, f) => sum + f.level, 0) / (fruitLevels.length * 100)) * 100}%` }}
              />
            </div>
          </div>
        </Card>

        {/* Recent Best Scores */}
        <Card className="p-4 bg-card border border-border">
          <h3 className="font-display font-semibold text-foreground mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-warning" />
            Level Best Scores
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {LEVELS.slice(0, 20).map(level => {
              const bestScore = getLevelBestScore(level.id);
              const isCompleted = completedLevels.includes(level.id);
              
              return (
                <div 
                  key={level.id}
                  className={`flex items-center justify-between p-2 rounded-lg ${
                    isCompleted ? 'bg-success/10' : 'bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{level.targetEmoji}</span>
                    <span className="font-body text-sm text-foreground">
                      Level {level.id}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isCompleted && (
                      <Star className="w-4 h-4 text-warning fill-warning" />
                    )}
                    <span className="font-display font-semibold text-foreground">
                      {bestScore > 0 ? bestScore.toLocaleString() : '-'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
